import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import Dashboard from '@/pages/Dashboard';
import RawMaterials from '@/pages/RawMaterials';
import RawMaterialInventory from '@/pages/RawMaterialInventory';
import BOM from '@/pages/BOM';
import FinishedGoods from '@/pages/FinishedGoods';
import PurchaseOrders from '@/pages/PurchaseOrders';
import PurchaseRequisitions from '@/pages/PurchaseRequisitions';
import SalesOrders from '@/pages/SalesOrders';
import ProductionOrders from '@/pages/ProductionOrders';
import Production from '@/pages/Production';
import CompletedProduction from '@/pages/CompletedProduction';
import Reports from '@/pages/Reports';
import Users from '@/pages/Users';
import RolePermissions from '@/pages/RolePermissions';
import Customers from '@/pages/Customers';
import WarehouseLocations from '@/pages/WarehouseLocations';
import CatalogInfo from '@/pages/CatalogInfo';
import BrochureMaker from '@/pages/BrochureMaker';
import Layout from '@/components/Layout';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const hasViewPerm = (user, module) => {
  if (user?.role === 'admin') return true;
  const perms = user?.permissions || {};
  if (typeof perms !== 'object' || Array.isArray(perms)) return false;
  return (perms[module] || []).includes('view');
};

const PermGuard = ({ user, module, children, fallback = "/" }) => {
  if (!hasViewPerm(user, module)) return <Navigate to={fallback} />;
  return children;
};

// Dashboard guard: redirect to first available page if no dashboard permission
const FALLBACK_ROUTES = [
  { path: '/raw-material-inventory', module: 'raw_material_inventory' },
  { path: '/raw-materials', module: 'raw_materials' },
  { path: '/production', module: 'production' },
  { path: '/sales-orders', module: 'sales_orders' },
  { path: '/bom', module: 'bom' },
];

const DashboardGuard = ({ user, children }) => {
  if (hasViewPerm(user, 'dashboard')) return children;
  const first = FALLBACK_ROUTES.find(r => hasViewPerm(user, r.module));
  return <Navigate to={first?.path || '/login'} />;
};

export const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Call backend to clear httpOnly auth cookie
    axios.post(`${API}/auth/logout`).catch(() => { });
  }
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only redirect if the 401 comes from authenticated endpoints, NOT the login page request itself
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      if (!isLoginRequest) {
        setAuthToken(null);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      setAuthToken(token);
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      // Refresh permissions from server so admin changes take effect without re-login
      axios.get(`${API}/users/me/permissions`).then(res => {
        const updated = { ...parsed, permissions: res.data.permissions, assigned_warehouses: res.data.assigned_warehouses || parsed.assigned_warehouses || [] };
        setUser(updated);
        localStorage.setItem('user', JSON.stringify(updated));
      }).catch(() => { });
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    setAuthToken(token);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setAuthToken(null);
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/forgot-password"
            element={!user ? <ForgotPassword /> : <Navigate to="/" />}
          />
          <Route
            path="/"
            element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          >
            <Route index element={<DashboardGuard user={user}><Dashboard user={user} /></DashboardGuard>} />
            <Route path="raw-materials" element={<PermGuard user={user} module="raw_materials"><RawMaterials user={user} /></PermGuard>} />
            <Route path="raw-material-inventory" element={<PermGuard user={user} module="raw_material_inventory"><RawMaterialInventory user={user} /></PermGuard>} />
            <Route path="bom" element={<PermGuard user={user} module="bom"><BOM user={user} /></PermGuard>} />
            <Route path="finished-goods" element={<PermGuard user={user} module="finished_goods"><FinishedGoods user={user} /></PermGuard>} />
            <Route path="purchase-orders" element={<PermGuard user={user} module="purchase_orders"><PurchaseOrders user={user} /></PermGuard>} />
            <Route path="purchase-requisitions" element={<PermGuard user={user} module="purchase_orders"><PurchaseRequisitions user={user} /></PermGuard>} />
            <Route path="sales-orders" element={<PermGuard user={user} module="sales_orders"><SalesOrders user={user} /></PermGuard>} />
            <Route path="warehouse-locations" element={<PermGuard user={user} module="warehouse_locations"><WarehouseLocations user={user} /></PermGuard>} />
            <Route path="customers" element={<PermGuard user={user} module="customers"><Customers user={user} /></PermGuard>} />
            <Route path="production-orders" element={<PermGuard user={user} module="production"><ProductionOrders user={user} /></PermGuard>} />
            <Route path="production" element={<PermGuard user={user} module="production"><Production user={user} /></PermGuard>} />
            <Route path="completed-production" element={<PermGuard user={user} module="production"><CompletedProduction user={user} /></PermGuard>} />
            <Route path="reports" element={<PermGuard user={user} module="reports"><Reports user={user} /></PermGuard>} />
            <Route path="catalog-info" element={<PermGuard user={user} module="catalog"><CatalogInfo user={user} /></PermGuard>} />
            <Route path="brochure-maker" element={<PermGuard user={user} module="catalog"><BrochureMaker user={user} /></PermGuard>} />
            {user?.role === 'admin' && <Route path="users" element={<Users user={user} />} />}
            {user?.role === 'admin' && <Route path="roles" element={<RolePermissions />} />}
          </Route>
          <Route path="*" element={<Navigate to={user ? "/" : "/login"} />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;